import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'

const DEFAULT_PAGE_SIZE = 20
const DEFAULT_DEBOUNCE_MS = 300

/**
 * useInfiniteOptions — Powered by TanStack Query useInfiniteQuery
 *
 * Provides paginated, searchable dropdown options with automatic request deduplication,
 * caching, and seamless infinite scrolling.
 */
export function useInfiniteOptions({
  fetchOptions,
  staticOptions,
  pageSize = DEFAULT_PAGE_SIZE,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  enabled = true,
  seedOption = null,
  minSearchLength = 3,
}) {
  const isAsync = typeof fetchOptions === 'function' && !staticOptions

  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const sentinelRef = useRef(null)
  const observerRef = useRef(null)

  // Debounce search input
  useEffect(() => {
    if (!isAsync) return
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim())
    }, debounceMs)
    return () => clearTimeout(timer)
  }, [search, isAsync, debounceMs])

  const isSearchShort = Boolean(
    isAsync &&
      minSearchLength > 0 &&
      debouncedSearch.length > 0 &&
      debouncedSearch.length < minSearchLength
  )

  const isQueryEnabled = Boolean(
    isAsync && enabled && !isSearchShort
  )

  // Derive stable key from fetchOptions
  const fetcherName = fetchOptions?.name || 'anonymous_fetcher'

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isQueryLoading,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['options', 'infinite', fetcherName, debouncedSearch, pageSize],
    queryFn: async ({ pageParam = 1, signal }) => {
      const result = await fetchOptions(debouncedSearch, pageParam, pageSize, { signal })
      const rawItems = Array.isArray(result?.items)
        ? result.items
        : Array.isArray(result)
        ? result
        : []
      const more = Boolean(result?.hasMore ?? (rawItems.length === pageSize))
      return { items: rawItems, hasMore: more }
    },
    getNextPageParam: (lastPage, allPages) => {
      return lastPage?.hasMore ? allPages.length + 1 : undefined
    },
    initialPageParam: 1,
    enabled: isQueryEnabled,
    staleTime: 1000 * 60 * 5, // 5 min cache
  })

  // IntersectionObserver to auto-fetch next page when sentinel is in view
  useEffect(() => {
    if (!isAsync || !hasNextPage || isFetchingNextPage) return
    if (observerRef.current) observerRef.current.disconnect()

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { threshold: 0.1 }
    )

    const sentinel = sentinelRef.current
    if (sentinel) observerRef.current.observe(sentinel)
    return () => observerRef.current?.disconnect()
  }, [isAsync, hasNextPage, isFetchingNextPage, fetchNextPage])

  // Extract flattened items from query pages
  const items = useMemo(() => {
    if (!isAsync) return staticOptions || []

    const fetchedItems = data?.pages?.flatMap((page) => page.items) || []

    // If no fetched items yet but we have a pre-seeded option in edit mode, show seed
    if (fetchedItems.length === 0 && seedOption?.value && seedOption?.label) {
      return [{ value: String(seedOption.value), label: seedOption.label }]
    }

    return fetchedItems
  }, [isAsync, staticOptions, data?.pages, seedOption])

  const reset = useCallback(() => {
    setSearch('')
    setDebouncedSearch('')
  }, [])

  return {
    items,
    search,
    setSearch,
    isLoading: isQueryLoading || isFetchingNextPage,
    hasMore: Boolean(hasNextPage),
    sentinelRef,
    reset,
    isSearchShort,
    minSearchLength,
    refetch,
  }
}
